<template>
    <div class="matches-overview">
        <div class="matches-overview__title">
            <span class="matches-overview__bar custom-orange" />
            {{ title }}
            <span class="matches-overview__hint">
                <span><v-icon x-small color="orange darken-2">mdi-arrow-up</v-icon> επόμενοι αγώνες</span>
                <span><v-icon x-small color="orange darken-2">mdi-arrow-down</v-icon> αποτελέσματα</span>
            </span>
            <button type="button" class="matches-overview__all" @click="$emit('show-all')">
                Όλοι οι αγώνες
                <v-icon x-small color="orange darken-2">mdi-arrow-right</v-icon>
            </button>
        </div>

        <div v-for="strip in strips" :key="strip.key" class="matches-overview__row">
            <template v-if="strip.matches.length">
                <v-btn
                    v-show="hasMouse && !$vuetify.breakpoint.xs && canScroll[strip.key] && canScroll[strip.key].left"
                    fab
                    x-small
                    color="white"
                    class="matches-overview__arrow matches-overview__arrow--left"
                    @click="scroll(strip.key, -1)"
                >
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <div :ref="`strip-${strip.key}`" class="matches-overview__strip" @scroll.passive="updateScroll(strip.key)">
                    <compact-match-card
                        v-for="match in strip.matches"
                        :key="match.id"
                        :match="match"
                        :live="isLive(match)"
                        :variant="strip.key === 'results' ? 'result' : 'upcoming'"
                        class="matches-overview__card"
                    />
                </div>
                <v-btn
                    v-show="hasMouse && !$vuetify.breakpoint.xs && canScroll[strip.key] && canScroll[strip.key].right"
                    fab
                    x-small
                    color="white"
                    class="matches-overview__arrow matches-overview__arrow--right"
                    @click="scroll(strip.key, 1)"
                >
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
            </template>
            <div v-else class="matches-overview__empty">{{ strip.empty }}</div>
        </div>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import CompactMatchCard from '@/components/CompactMatchCard'
    import { isMatchLive, refreshLiveStatuses, withLiveData, pollWhileVisible, LIVE_TICK_MS } from '@/utils/matchLive'

    function time(match) {
        return match.matchDatetime ? momentTimezone(match.matchDatetime).valueOf() : 0
    }

    // Match summary of the team page and the news page: two horizontal strips, next matches (live first) and results (latest first)
    export default {
        components: { CompactMatchCard },
        props: {
            matches: {
                type: Array,
                default: () => []
            },
            title: {
                type: String,
                default: 'Πρόγραμμα'
            }
        },
        data() {
            return {
                // a mouse: arrows; touch screens and phone widths swipe
                hasMouse: typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)').matches : true,
                now: Date.now(),
                stopTick: null,
                // { upcoming: { left, right }, results: { left, right } }: which arrows to show
                canScroll: {}
            }
        },
        computed: {
            // live score and end of game from the stat logs, ahead of the next list reload
            liveMatches() {
                return this.matches.map(match => withLiveData(match, this.now))
            },
            upcoming() {
                return this.liveMatches
                    .filter(match => match.pending || this.isLive(match))
                    // live first, postponed last (their original date is usually the oldest), the rest by date
                    .sort((a, b) => (this.isLive(b) - this.isLive(a)) || (Boolean(a.postponed) - Boolean(b.postponed)) || (time(a) - time(b)))
            },
            results() {
                return this.liveMatches
                    .filter(match => !match.pending && !this.isLive(match))
                    .sort((a, b) => time(b) - time(a))
            },
            strips() {
                return [
                    { key: 'upcoming', matches: this.upcoming, empty: 'Δεν υπάρχουν επόμενοι αγώνες σε αυτή τη φάση.' },
                    { key: 'results', matches: this.results, empty: 'Δεν υπάρχουν αποτελέσματα ακόμα σε αυτή τη φάση.' }
                ]
            }
        },
        watch: {
            matches: {
                handler() {
                    refreshLiveStatuses(this.matches)
                    this.$nextTick(this.updateAllScroll)
                },
                immediate: true
            }
        },
        created() {
            this.stopTick = pollWhileVisible(() => {
                this.now = Date.now()
                refreshLiveStatuses(this.matches, this.now)
            }, LIVE_TICK_MS)
        },
        mounted() {
            window.addEventListener('resize', this.updateAllScroll, { passive: true })
            this.$nextTick(this.updateAllScroll)
        },
        beforeDestroy() {
            this.stopTick()
            window.removeEventListener('resize', this.updateAllScroll)
        },
        methods: {
            isLive(match) {
                return isMatchLive(match, this.now)
            },
            stripEl(key) {
                const ref = this.$refs[`strip-${key}`]
                return Array.isArray(ref) ? ref[0] : ref
            },
            updateScroll(key) {
                const el = this.stripEl(key)
                const state = el
                    ? { left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 }
                    : { left: false, right: false }
                this.$set(this.canScroll, key, state)
            },
            updateAllScroll() {
                this.strips.forEach(strip => this.updateScroll(strip.key))
            },
            scroll(key, direction) {
                const el = this.stripEl(key)
                if (el) {
                    el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.8, 220), behavior: 'smooth' })
                }
            }
        }
    }
</script>

<style scoped>
.matches-overview {
    font-family: Ubuntu, Arial, sans-serif;
    text-align: left;
}

.matches-overview__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;
    margin-bottom: 10px;
    padding: 6px 14px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-size: 1.05rem;
    font-weight: 800;
}

.matches-overview__bar {
    width: 5px;
    height: 20px;
    border-radius: 2px;
}

.matches-overview__hint {
    display: inline-flex;
    gap: 10px;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 600;
}

.matches-overview__all {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-left: auto;
    color: #e65100;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
}

/* ---------- Strips ---------- */
.matches-overview__row {
    position: relative;
}

.matches-overview__row + .matches-overview__row {
    margin-top: 0;
}

/* Horizontal scroll with snapping; the padding leaves room for the card shadow and hover lift */
.matches-overview__strip {
    display: flex;
    gap: 12px;
    padding: 4px 2px 6px;
    overflow-x: auto;
    scroll-behavior: smooth;
    scroll-snap-type: x proximity;

    /* no visible scrollbar: the arrows are the hint that there is more */
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.matches-overview__strip::-webkit-scrollbar {
    display: none;
}

.matches-overview__card {
    flex: 0 0 auto;
    scroll-snap-align: start;
}

.matches-overview__arrow {
    position: absolute !important;
    top: 50%;
    z-index: 2;
    transform: translateY(-50%);
}

.matches-overview__arrow--left {
    left: -10px;
}

.matches-overview__arrow--right {
    right: -10px;
}

.matches-overview__empty {
    margin-bottom: 8px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #fff;
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.9rem;
    font-weight: 600;
    text-align: center;
}
</style>
